import { objectSpread2 as _objectSpread2, extends as _extends } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import React, { useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { PaginationModule } from './PaginationModule.js';
import { ResponsiveDataList } from '../../Table/ResponsiveDataList.js';
import { noop } from '../../../utils/noop.js';
import { useContainerProps } from '../../../hooks/useContainerProps.js';
import { getPagingInfo } from '../../Pagination.js';
/** renders a list module intended for use inside a section */

var ListModule = function ListModule(props) {
  props = useContainerProps(props);
  var _props = props,
      actionBar = _props.actionBar,
      _props$actions = _props.actions,
      actions = _props$actions === void 0 ? [] : _props$actions,
      actionsWidth = _props.actionsWidth,
      _props$activeFilters = _props.activeFilters,
      activeFilters = _props$activeFilters === void 0 ? {} : _props$activeFilters,
      activeSearch = _props.activeSearch,
      activeSort = _props.activeSort,
      _props$activePage = _props.activePage,
      activePage = _props$activePage === void 0 ? 1 : _props$activePage,
      _props$collection = _props.collection,
      collection = _props$collection === void 0 ? [] : _props$collection,
      collectionMeta = _props.collectionMeta,
      downloadAction = _props.downloadAction,
      downloadIcon = _props.downloadIcon,
      downloadLabel = _props.downloadLabel,
      downloadUrl = _props.downloadUrl,
      dropdownActions = _props.dropdownActions,
      _props$fields = _props.fields,
      fields = _props$fields === void 0 ? [{
    name: 'name'
  }] : _props$fields,
      _props$keyField = _props.keyField,
      keyField = _props$keyField === void 0 ? 'id' : _props$keyField,
      _props$loadCollection = _props.loadCollection,
      loadCollection = _props$loadCollection === void 0 ? noop : _props$loadCollection,
      _props$onPageChange = _props.onPageChange,
      onPageChange = _props$onPageChange === void 0 ? noop : _props$onPageChange,
      _props$options = _props.options,
      options = _props$options === void 0 ? {} : _props$options,
      _props$onSortChange = _props.onSortChange,
      onSortChange = _props$onSortChange === void 0 ? noop : _props$onSortChange,
      selectRow = _props.selectRow,
      selectedItems = _props.selectedItems,
      _props$setActivePage = _props.setActivePage,
      setActivePage = _props$setActivePage === void 0 ? noop : _props$setActivePage,
      _props$setActiveSort = _props.setActiveSort,
      setActiveSort = _props$setActiveSort === void 0 ? noop : _props$setActiveSort,
      _props$showDownloadLi = _props.showDownloadLink,
      showDownloadLink = _props$showDownloadLi === void 0 ? false : _props$showDownloadLi,
      _props$useListFilteri = _props.useListFiltering,
      useListFiltering = _props$useListFilteri === void 0 ? false : _props$useListFilteri,
      _props$useCoreTable = _props.useCoreTable,
      useCoreTable = _props$useCoreTable === void 0 ? true : _props$useCoreTable;
  useEffect(function () {
    if (useListFiltering) {
      loadCollection({
        filters: _objectSpread2(_objectSpread2({}, activeFilters), activeSearch && {
          keyword: activeSearch
        })
      });
    }
  }, [activeFilters, activeSearch, useListFiltering, loadCollection]);
  var handleSortChange = useCallback(function (sortValue, sortDirection) {
    if (activeSort && sortValue === activeSort.value && sortDirection === activeSort.direction) {
      return;
    }

    setActiveSort({
      value: sortValue,
      direction: sortDirection
    });
    setActivePage(1);
    onSortChange(sortValue, sortDirection);
    onPageChange(1);
  }, [activeSort, onPageChange, onSortChange, setActivePage, setActiveSort]);
  useEffect(function () {
    var defaultSortField;

    if (!activeSort) {
      defaultSortField = fields.find(function (field) {
        return field.defaultSort;
      });

      if (defaultSortField) {
        var sortValue = defaultSortField.name;
        var sortDirection = defaultSortField.defaultSort === true ? 'asc' : defaultSortField.defaultSort;
        handleSortChange(sortValue, sortDirection);
      }
    }

    if (!defaultSortField) {
      loadCollection();
    }
  }, [activePage, activeSort === null || activeSort === void 0 ? void 0 : activeSort.value, activeSort === null || activeSort === void 0 ? void 0 : activeSort.direction, activeSort, fields.length, handleSortChange]); // eslint-disable-line react-hooks/exhaustive-deps

  var handlePageChange = function handlePageChange(page) {
    onPageChange(page);
    setActivePage(page);

    if (page === activePage) {
      // if pageSize changes, this will trigger a reload if the page number hasnt changed
      loadCollection();
    }
  };

  var tableOptions = Object.assign({
    bordered: false,
    hover: true,
    onSortChange: handleSortChange,
    sortName: activeSort && activeSort.value,
    sortOrder: activeSort && activeSort.direction,
    striped: false
  }, options);
  var totalCount = collectionMeta ? collectionMeta.total_count : 0;
  var pagingInfo = getPagingInfo({
    data: collection,
    totalCount: totalCount,
    activePage: activePage
  });
  return /*#__PURE__*/React.createElement(PaginationModule, _extends({}, props, {
    collection: collection,
    collectionMeta: collectionMeta,
    onPageChange: handlePageChange,
    selectedItems: selectedItems,
    setActivePage: setActivePage,
    loadCollection: noop,
    showDownloadLink: showDownloadLink,
    downloadAction: downloadAction,
    downloadIcon: downloadIcon,
    downloadLabel: downloadLabel,
    downloadUrl: downloadUrl,
    hidePagination: useCoreTable
  }), /*#__PURE__*/React.createElement(ResponsiveDataList, _extends({}, props, {
    data: collection,
    fields: fields,
    keyField: keyField,
    options: tableOptions,
    actionBar: actionBar,
    actions: actions,
    actionsWidth: actionsWidth,
    selectRow: selectRow,
    totalCount: totalCount,
    onPageChange: handlePageChange,
    pagingInfo: pagingInfo,
    useCoreTable: useCoreTable,
    dropdownActions: dropdownActions
  })));
};

ListModule.displayName = "ListModule";
ListModule.propTypes = {
  actionBar: PropTypes.array,
  actions: PropTypes.arrayOf(PropTypes.shape({
    type: PropTypes.string,
    selectMember: PropTypes.func,
    deleteMember: PropTypes.func,
    action: PropTypes.func,
    actionType: PropTypes.string,
    confirmTitle: PropTypes.string,
    confirmChildren: PropTypes.string,
    target: PropTypes.string,
    download: PropTypes["boolean"]
  })),
  actionsWidth: PropTypes.number,
  activeFilters: PropTypes.object,
  activePage: PropTypes.number,
  activeSearch: PropTypes.string,

  /** object/array/string that defines the sort order */
  activeSort: PropTypes.any,
  collection: PropTypes.array,

  /** define how the table rows are rendered
   *  (passed to ResponsiveDataList -> Table -> BootstrapTable component)
   *  documentation of additional BootstrapTable column props can be found at
   *  https://react-bootstrap-table.github.io/react-bootstrap-table2/docs/column-props.html */
  collectionMeta: PropTypes.object,
  downloadAction: PropTypes.func,
  downloadIcon: PropTypes.string,
  downloadLabel: PropTypes.string,
  downloadUrl: PropTypes.string,
  dropdownActions: PropTypes.bool,
  fields: PropTypes.arrayOf(PropTypes.shape({
    /** if set to true, then the list will be sorted by this column by default */
    defaultSort: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    formatter: PropTypes.func
  })),
  isLoadingData: PropTypes.bool,

  /** collection prop used as the key for each list item */
  keyField: PropTypes.string,
  loadCollection: PropTypes.func,
  onPageChange: PropTypes.func,

  /** callback(sortColumn, sortDirection) when page is changed */
  onSortChange: PropTypes.func,

  /** any table options are passed to ResponsiveDataList -> Table component */
  options: PropTypes.object,
  selectRow: PropTypes.object,
  selectedItems: PropTypes.object,
  setActivePage: PropTypes.func,
  setActiveSort: PropTypes.func,
  showDownloadLink: PropTypes.bool,
  useListFiltering: PropTypes.bool,
  resultsDisplayed: PropTypes.bool,
  useCoreTable: PropTypes.bool
};
export { ListModule };